import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { PageShell } from "../components/SiteChrome";

export function About() {
  return (
    <PageShell kicker="About" title="Coffee, without the cup." sub="Why we press espresso into a pellet.">
      <div className="doc fade-up">
        <h2>The idea</h2>
        <p>Kaffgum started with a simple observation: the slowest part of coffee is everything around the caffeine.
          Brewing, queueing, cooling, digesting. Caffeine absorbed through the lining of your mouth arrives in
          minutes — no cup required.</p>
        <h2>What's inside</h2>
        <ul>
          <li><b style={{ color: "var(--cream)" }}>80 mg caffeine</b> per piece — about one espresso.</li>
          <li><b style={{ color: "var(--cream)" }}>100 mg L-Theanine</b> — flattens the spike into something you can work through.</li>
          <li><b style={{ color: "var(--cream)" }}>Vitamins B6, B12 and Niacin</b> — keep the energy conversion running.</li>
          <li><b style={{ color: "var(--cream)" }}>0 g sugar</b> — a hard-panned sugarfree shell, polished smooth.</li>
        </ul>
        <h2>How it plays out</h2>
        <p>Flip the lid, chew steadily. The coating fractures and caffeine begins crossing the cheek lining within
          thirty seconds. By five minutes you're level; the effect holds for around ninety. Bin the piece whenever
          the flavour goes.</p>
        <p style={{ fontSize: 12, opacity: 0.7 }}>Not for under 18s, or anyone pregnant or sensitive to caffeine.</p>
      </div>
    </PageShell>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      await api.post("/contact", form);
      setOk(true);
      setMsg("Message sent — we'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setOk(false);
      setMsg(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <PageShell kicker="Contact" title="Say hello." sub="Questions, stockists, wholesale — we read everything.">
      <div className="wrap" style={{ paddingBottom: 60 }}>
        <div className="auth-card fade-up" style={{ maxWidth: 480 }}>
          <form onSubmit={submit} data-testid="contact-form">
            <label className="field"><span>Name</span>
              <input required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name-input" /></label>
            <label className="field"><span>Email</span>
              <input required type="email" value={form.email} onChange={set("email")} placeholder="name@email.com" data-testid="contact-email-input" /></label>
            <label className="field"><span>Message</span>
              <textarea required minLength={2} value={form.message} onChange={set("message")} placeholder="What's on your mind?" data-testid="contact-message-input" /></label>
            <p className={"form-msg " + (ok ? "ok" : msg ? "err" : "")} role="status" data-testid="contact-msg">{msg}</p>
            <button className="kbtn wide" type="submit" disabled={busy} data-testid="contact-submit-btn">
              {busy ? "Sending\u2026" : "Send message"}
            </button>
          </form>
        </div>
      </div>
    </PageShell>
  );
}

const FAQS = [
  ["How much caffeine is in one piece?", "80 mg — about one espresso. We recommend no more than three pieces a day."],
  ["How fast does it work?", "Caffeine crosses the lining of your mouth while you chew, so onset is around five minutes — far faster than a drink that has to be digested."],
  ["Is it sugarfree?", "Yes. The shell is hard-panned and polished over a sugarfree base. Zero grams of sugar."],
  ["How long should I chew?", "Chew steadily for at least five minutes to release the caffeine. Bin the piece whenever the flavour goes."],
  ["Who shouldn't chew Kaffgum?", "Anyone under 18, pregnant, or sensitive to caffeine. If in doubt, ask your doctor."],
  ["What payment methods do you accept?", "UPI, all major cards and netbanking via Razorpay. Payment is confirmed instantly and you'll get an email receipt."],
  ["When will my order ship?", "Orders are packed within 24 hours. Free delivery on orders over ₹499."],
];

export function FAQ() {
  return (
    <PageShell kicker="FAQ" title="Fair questions." sub="Everything people ask before their first chew.">
      <div className="doc fade-up" data-testid="faq-list">
        {FAQS.map(([q, a]) => (
          <details className="faq-item" key={q}>
            <summary>{q}</summary>
            <p>{a}</p>
          </details>
        ))}
      </div>
    </PageShell>
  );
}

export function Privacy() {
  return (
    <PageShell kicker="Legal" title="Privacy policy.">
      <div className="doc fade-up">
        <h2>What we collect</h2>
        <p>Your name, email, phone and shipping address when you place an order or create an account. Your cart is
          saved so you can pick up where you left off.</p>
        <h2>What we do with it</h2>
        <p>Fulfil orders, send order confirmations, and answer messages you send us. We never sell your data.</p>
        <h2>Payments</h2>
        <p>Payments are processed by Razorpay. Your card details never touch our servers.</p>
        <h2>Cookies</h2>
        <p>We use a single authentication cookie to keep you signed in. No third-party tracking.</p>
        <h2>Contact</h2>
        <p>Questions about your data? Reach us via the contact page and we'll respond within 48 hours.</p>
      </div>
    </PageShell>
  );
}

export function Terms() {
  return (
    <PageShell kicker="Legal" title="Terms of service.">
      <div className="doc fade-up">
        <h2>The product</h2>
        <p>Kaffgum is a caffeinated chewing gum. It is not intended to diagnose, treat, cure, or prevent any disease.
          Not for under 18s, or anyone pregnant or sensitive to caffeine.</p>
        <h2>Orders & payment</h2>
        <p>Prices are in Indian Rupees and include GST. An order is confirmed once payment is verified, at which point
          you'll receive an email confirmation.</p>
        <h2>Delivery & returns</h2>
        <p>Orders ship within 24 hours. Being a food product, opened packs cannot be returned; unopened packs can be
          returned within 14 days of delivery.</p>
        <h2>Liability</h2>
        <p>Consume responsibly. Do not exceed three pieces in a day and do not combine with other high-caffeine
          products.</p>
      </div>
    </PageShell>
  );
}

export function NotFound() {
  return (
    <PageShell>
      <div className="nf fade-up" data-testid="not-found-page">
        <div>
          <h1>404</h1>
          <p style={{ color: "var(--ink-dim)", margin: "10px 0 26px" }}>This page has been chewed and binned.</p>
          <Link className="kbtn" to="/" style={{ width: "auto" }} data-testid="notfound-home-btn">Back to the start</Link>
        </div>
      </div>
    </PageShell>
  );
}
